import classNames from 'classnames';
import FalconCardHeader from '../../../../../components/common/FalconCardHeader';
import PropTypes from 'prop-types';
import React from 'react';
import { Badge, Card, Col, Row } from 'react-bootstrap';
import IconButton from '../../../../../components/common/IconButton';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { saveFechasArray, getFechasArray } from '../../../../../utils/indexedDBUtils';

const FechaItem = ({ fecha, isLast, onSelectFecha, highlighted }) => {
    const {
        fechaCorta,
        diaSemana,
        totalKM,
        totalVehiculos,
        percentage,
        fechaFormateada
    } = fecha;

    return (
        <div>
            <Row
                className={classNames(
                    'position-relative align-items-center py-2 px-3 g-0',
                    { 'border-bottom border-200': !isLast }
                )}
                onClick={(e) => {
                    e.preventDefault();
                    onSelectFecha({
                        fecha: fechaCorta,
                        diaSemana: diaSemana,
                        totalKM: totalKM,
                        totalVehiculos: totalVehiculos
                    });
                }}
            >
                {/* ✅ COLUMNA 1: DÍA DE LA SEMANA */}
                <Col xs={3}>
                    <a
                        className={classNames('fw-normal label-detail-card', {
                            'fw-bold': highlighted
                        })}
                        href="#!"
                        onClick={(e) => {
                            e.preventDefault();
                            onSelectFecha({
                                fecha: fechaCorta,
                                diaSemana: diaSemana,
                                totalKM: totalKM,
                                totalVehiculos: totalVehiculos
                            });
                        }}
                    >
                        {diaSemana}
                    </a>
                </Col>

                {/* ✅ COLUMNA 2: FECHA */}
                <Col xs={3}>
                    <div className="fw-normal label-detail-card text-primary" style={{ fontSize: '14px' }}>
                        {fechaFormateada}
                    </div>
                </Col>

                {/* ✅ COLUMNA 3: PORCENTAJE */}
                <Col xs={2} className="text-center">
                    <Badge pill bg="200" className="ms-2 text-primary text-primary" style={{ fontSize: '12px' }}>
                        {percentage}%
                    </Badge>
                </Col>

                {/* ✅ COLUMNA 4: KILÓMETROS */}
                <Col xs={3} className="text-end">
                    <div className="fw-normal label-detail-card text-primary" style={{ fontSize: '14px' }}>
                        {totalKM.toFixed(0)}km
                    </div>
                </Col>

                {/* ✅ COLUMNA 5: FLECHA */}
                <Col xs={1} className="text-end">
                    <a className="text-800"
                        href="#!"
                        onClick={(e) => {
                            e.preventDefault();
                            onSelectFecha({
                                fecha: fechaCorta,
                                diaSemana: diaSemana,
                                totalKM: totalKM,
                                totalVehiculos: totalVehiculos
                            });
                        }}>
                        <FontAwesomeIcon icon={faArrowRight} size="sm" />
                    </a>
                </Col>
            </Row>
        </div>
    );
};

FechaItem.propTypes = {
    fecha: PropTypes.shape({
        fechaCorta: PropTypes.string.isRequired,
        fechaCompleta: PropTypes.string.isRequired,
        diaSemana: PropTypes.string.isRequired,
        totalKM: PropTypes.number.isRequired,
        totalVehiculos: PropTypes.number.isRequired,
        percentage: PropTypes.number.isRequired,
        fechaFormateada: PropTypes.string.isRequired
    }).isRequired,
    isLast: PropTypes.bool,
    onSelectFecha: PropTypes.func.isRequired,
    highlighted: PropTypes.bool
};

/**
 * Componente que muestra kilómetros agrupados por fecha
 * Similar a VehiculoKilometraje pero para datos por fecha
 */
const FechaKilometraje = ({
    data,
    onSelectFecha,
    highlightedFecha,
    fechaFiltrada,
    handleFiltro,
    title = "Kilómetros por Fecha"
}) => {

    // ✅ PROCESAMIENTO DE DATOS (permanece igual)
    const fechasConKM = data.reduce((acc, registro) => {
        const fechaKey = registro.fechaCorta;

        if (!acc[fechaKey]) {
            const fechaObj = dayjs(registro.fecha);
            const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

            acc[fechaKey] = {
                fechaCorta: fechaKey,
                fechaCompleta: registro.fecha,
                diaSemana: diasSemana[fechaObj.day()],
                fechaFormateada: fechaObj.format('DD/MM/YYYY'),
                totalKM: 0,
                totalVehiculos: new Set(),
                registros: []
            };
        }

        acc[fechaKey].totalKM += parseFloat(registro.promedio_distancia || 0);
        acc[fechaKey].totalVehiculos.add(registro.vehiculoUid);
        acc[fechaKey].registros.push(registro);

        return acc;
    }, {});

    const fechasArray = Object.values(fechasConKM).map(fecha => {
        const totalGlobalKM = Object.values(fechasConKM).reduce((sum, f) => sum + f.totalKM, 0);
        const percentage = totalGlobalKM > 0 ? ((fecha.totalKM / totalGlobalKM) * 100) : 0;

        return {
            ...fecha,
            totalVehiculos: fecha.totalVehiculos.size,
            percentage: percentage.toFixed(0), // ✅ Sin decimales en porcentaje
            id: fecha.fechaCorta
        };
    });

    fechasArray.sort((a, b) => dayjs(b.fechaCompleta).diff(dayjs(a.fechaCompleta)));

    const totalKilometros = fechasArray.reduce((total, fecha) => total + fecha.totalKM, 0);
    const totalFechas = fechasArray.length;

    // Estado para manejar los datos almacenados
    const [dataListCard, setDataListCard] = React.useState([]);

    // Effect para manejar el almacenamiento y recuperación de datos
    React.useEffect(() => {
        const handleDataStorage = async () => {
            // Ningun filtro seleccionado
            if (!highlightedFecha) {
                try {
                    // Almacena en IndexedDB (con fallback a localStorage)
                    await saveFechasArray(fechasArray);
                    // Listamos en la tabla todos los datos encontrados 
                    setDataListCard(fechasArray);
                } catch (error) {
                    console.error('Error al guardar datos:', error);
                    // En caso de error, usar los datos directamente
                    setDataListCard(fechasArray);
                }
            } else {
                try {
                    // Si ya fueron filtrados, obtenemos todos los datos almacenados
                    const storedData = await getFechasArray();
                    setDataListCard(storedData.length > 0 ? storedData : fechasArray);
                } catch (error) {
                    console.error('Error al recuperar datos:', error);
                    // Fallback: usar los datos actuales
                    setDataListCard(fechasArray);
                }
            }
        };

        if (fechasArray.length > 0) {
            handleDataStorage();
        } else {
            setDataListCard([]);
        }
    }, [fechasArray, highlightedFecha]);

    //-------------------------------------------------------------------------------


    return (
        <Card className="h-100">
            <FalconCardHeader
                title={fechaFiltrada ? `${title}: ${fechaFiltrada}` : title}
                light
                titleTag="h6"
                endEl={fechaFiltrada && (
                    <IconButton
                        onClick={handleFiltro}
                        icon="times-circle"
                        size="sm"
                        variant="falcon-default">
                        Remover filtro
                    </IconButton>
                )}
            />

            <Card.Body className="p-0">

                {/* ✅ HEADER ACTUALIZADO CON NUEVAS COLUMNAS */}
                <div className="px-3 py-2 bg-white border-bottom">
                    <Row>
                        <Col className='form-col-label' xs={3}>DÍA</Col>
                        <Col className='form-col-label' xs={3}>FECHA</Col>
                        <Col className="form-col-label text-center" xs={2} >%</Col>
                        <Col className="form-col-label text-end" xs={3}>KM</Col>
                        <Col className="form-col-label text-end" xs={1}></Col>
                    </Row>
                </div>

                <div className="overflow-auto" style={{ maxHeight: '280px' }}>
                    {dataListCard.length > 0 ? (
                        dataListCard.map((fecha, index) => (
                            <FechaItem
                                fecha={fecha}
                                isLast={index === fechasArray.length - 1}
                                key={fecha.id}
                                onSelectFecha={onSelectFecha}
                                highlighted={highlightedFecha === fecha.fechaCorta}
                            />
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted">
                            <div style={{ fontSize: '12px' }}>No hay datos disponibles</div>
                        </div>
                    )}
                </div>
            </Card.Body>

            {/* ✅ FOOTER SIMPLIFICADO */}
            <Card.Footer className=''>
                <Row className='text-center border-top border-200 pt-2'>
                    <Col xs={4}>
                        <div className="fw-bold label-detail-card" style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`Total Días: ${totalFechas}`}>Total Días: {totalFechas}</div>
                    </Col>
                    <Col xs={4}>
                        <div className="fw-bold label-detail-card" style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`Total KM: ${totalKilometros.toFixed(0)} km`}>Total KM: {totalKilometros.toFixed(0)} km</div>
                    </Col>
                    <Col xs={4}>
                        <div className="fw-bold label-detail-card" style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`Promedio/Día: ${totalFechas > 0 ? (totalKilometros / totalFechas).toFixed(0) : '0'} km`}>Promedio/Día: {totalFechas > 0 ? (totalKilometros / totalFechas).toFixed(0) : '0'} km
                        </div>
                    </Col>
                </Row>
            </Card.Footer>
        </Card>
    );
};

FechaKilometraje.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        fecha: PropTypes.string.isRequired,
        fechaCorta: PropTypes.string.isRequired,
        vehiculoUid: PropTypes.string.isRequired,
        promedio_distancia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })).isRequired,
    onSelectFecha: PropTypes.func.isRequired,
    highlightedFecha: PropTypes.string,
    fechaFiltrada: PropTypes.string,
    handleFiltro: PropTypes.func,
    title: PropTypes.string
};

export default FechaKilometraje;